import React from 'react';
import styled from '@emotion/styled';
import { Menu, MenuItem } from '@mui/material';
import { DotsIcon } from '@/components/common/comment/commentIcons';
import { COMMENT_COLOR } from '@/components/common/comment/commentTokens';

/**
 * 글·댓글의 ⋯ 메뉴 하나(2026-10-11 D3) — 글 상세(내 글: 수정·삭제), 남의 글·댓글(신고·차단), 내 댓글(수정·삭제)이
 * 세 가지 모양(세로 점 MUI 단추 / 손으로 만든 드롭다운 / 댓글 ⋯)이던 것을 댓글 ⋯ 모양 하나로 맞춘다.
 */

/** ⋯ 단추 — 28px 칸 + 18px 아이콘, 가운데 정렬. 손가락용 누르는 영역은 ::after 로 44px 로 넓힌다 */
export const MoreMenuButton = styled.button`
    position: relative;
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: none;
    color: ${COMMENT_COLOR.faint};
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    svg {
        display: block;
        width: 18px;
        height: 18px;
    }

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 44px;
        height: 44px;
        transform: translate(-50%, -50%);
    }

    @media (hover: hover) {
        &:hover {
            background: ${COMMENT_COLOR.hover};
        }
    }
`;

export interface MoreMenuItem {
    label: string;
    onClick: () => void;
    /** 삭제·신고처럼 되돌리기 어려운 동작 — 빨강(#E11D48 하나) */
    danger?: boolean;
}

/** 메뉴 항목 — 글자 14px, 한 줄 44px(누름 영역). MUI 는 600px 이상에서 최소 높이를 풀어 33px 가 되므로 같이 막는다 */
const itemSx = { fontSize: 14, minHeight: 44, '@media (min-width: 600px)': { minHeight: 44 } };

export interface MoreMenuPopupProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    items: MoreMenuItem[];
}

/** 단추 아래 작은 팝업 — 본문 스크롤을 막지 않는다(폰에서 키보드가 뜬 채 열면 막기와 키보드 내림 위치 되돌리기가 겹쳐 머리줄이 밀렸다) */
export const MoreMenuPopup: React.FC<MoreMenuPopupProps> = ({ anchorEl, onClose, items }) => (
    <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
        {items.map((item) => (
            <MenuItem
                key={item.label}
                sx={item.danger ? { ...itemSx, color: COMMENT_COLOR.liked } : itemSx}
                onClick={() => {
                    onClose();
                    item.onClick();
                }}
            >
                {item.label}
            </MenuItem>
        ))}
    </Menu>
);

/** ⋯ 단추 + 팝업 한 벌 — 내 글·내 댓글의 수정·삭제처럼 항목만 넘기면 되는 곳 */
export const MoreMenu: React.FC<{ label: string; items: MoreMenuItem[] }> = ({ label, items }) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    return (
        <>
            <MoreMenuButton type="button" aria-label={label} aria-haspopup="menu" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <DotsIcon />
            </MoreMenuButton>
            <MoreMenuPopup anchorEl={anchorEl} onClose={() => setAnchorEl(null)} items={items} />
        </>
    );
};

export default MoreMenu;
